import { z } from 'zod';
import { emailSchema, moneyAmountSchema, uuidSchema, visuelSchema } from './common.js';
/**
 * Catalogue (ADR-06) et fournisseurs.
 *
 * Produits et services partagent le même schéma : seul `kind` les distingue,
 * et un service est simplement un article sans suivi de stock.
 */
export const itemKindSchema = z.enum(['product', 'service']);
export const itemStatusSchema = z.enum(['active', 'inactive', 'discontinued']);
/** Unités de mesure — un service se facture à l'heure ou à la journée. */
export const UNITS = ['piece', 'hour', 'day', 'kg', 'g', 'l', 'm', 'm2', 'forfait'];
export const UNIT_LABELS = {
    piece: 'Pièce',
    hour: 'Heure',
    day: 'Jour',
    kg: 'Kilogramme',
    g: 'Gramme',
    l: 'Litre',
    m: 'Mètre',
    m2: 'Mètre carré',
    forfait: 'Forfait',
};
const optionalText = (max) => z
    .string()
    .trim()
    .max(max, `Au plus ${max} caractères`)
    .optional()
    .transform((v) => (v === '' ? undefined : v));
export const itemInputSchema = z
    .object({
    id: uuidSchema.optional(),
    kind: itemKindSchema.default('product'),
    sku: optionalText(40),
    barcode: optionalText(40),
    name: z.string().trim().min(2, 'Au moins 2 caractères').max(160),
    description: optionalText(4000),
    categoryId: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
    /**
     * L'article paraît-il sur le SITE VITRINE ?
     *
     * ⭐ Le catalogue de gestion et la vitrine ne montrent pas la même chose :
     * on vend au comptoir des articles qu'on n'expose pas en ligne.
     *
     * ⚠️ Faux par défaut. Publier un site ne doit pas déverser tout le
     * catalogue sur la place publique à l'insu du commerçant — une omission se
     * corrige d'un clic, une divulgation ne se rattrape pas.
     */
    visibleSurSite: z.boolean().default(false),
    /**
     * Taux de TVA appliqué à cet article.
     *
     * ⚠️ Le taux sera COPIÉ dans la ligne de vente, jamais lu par jointure : si
     * l'État change la TVA l'an prochain, les factures de cette année doivent
     * continuer d'afficher le taux réellement appliqué.
     */
    taxId: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
    supplierId: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
    unit: z.enum(UNITS).default('piece'),
    // Entiers dans la plus petite unité de la devise (ADR-10).
    purchasePrice: moneyAmountSchema.min(0, 'Ne peut pas être négatif').default(0),
    salePrice: moneyAmountSchema.min(0, 'Ne peut pas être négatif').default(0),
    currencyCode: z.string().length(3).toUpperCase().optional(),
    /**
     * Durée d'une PRESTATION, en minutes.
     *
     * ⭐ C'est elle qui rend l'agenda possible : sans durée, on ne peut ni
     * découper une journée en créneaux, ni savoir quand le suivant commence.
     *
     * ⚠️ Facultative, y compris pour un service : toutes les prestations ne se
     * prennent pas sur rendez-vous — une retouche faite au comptoir se facture
     * sans occuper de créneau. Un service sans durée reste vendable, il n'est
     * simplement pas proposable à l'agenda.
     */
    durationMinutes: z.coerce
        .number()
        .int('Durée en minutes entières')
        .min(5, 'Au moins 5 minutes')
        .max(600, 'Au plus 10 heures')
        .optional()
        .or(z.literal('').transform(() => undefined))
        .or(z.null().transform(() => undefined)),
    trackStock: z.boolean().default(true),
    minStockLevel: z.coerce.number().min(0, 'Ne peut pas être négatif').default(0),
    status: itemStatusSchema.default('active'),
    /**
     * Stock de départ, à la CRÉATION seulement.
     *
     * ⚠️ Ce n'est pas une colonne de l'article : le serveur en fait un
     * mouvement d'entrée. Le porter dans la charge de création plutôt que
     * dans un second appel est ce qui rend la saisie possible HORS LIGNE —
     * un appel séparé viserait un article que le serveur ne connaît pas
     * encore, puisque sa création est elle-même en file d'attente.
     */
    initialStock: z.coerce.number().min(0).optional(),
    /** Jusqu'à 5 visuels. Le premier sert de vignette dans les listes. */
    images: z.array(visuelSchema).max(5, 'Cinq photos au maximum').optional(),
    imageUrl: visuelSchema.optional().or(z.literal('').transform(() => undefined)),
})
    // Cohérence reflétée par la contrainte SQL : un service n'a pas de stock.
    .transform((v) => (v.kind === 'service' ? { ...v, trackStock: false } : v));
/**
 * Le prix d'achat est OBLIGATOIRE pour un produit.
 *
 * ⭐ Sans lui, le bénéfice net du tableau de bord compte l'article pour zéro
 * et se trouve surestimé d'autant. Une prestation en est dispensée : une
 * coupe ou un massage n'ont pas de prix d'achat.
 *
 * ⚠️ La règle n'est PAS dans `itemInputSchema`, délibérément. Ce schéma valide
 * aussi les écritures faites HORS LIGNE, à leur arrivée au serveur : une
 * modification en attente sur un produit ancien, sans prix, y serait refusée
 * — et la synchronisation de l'appareil resterait bloquée. La règle s'applique
 * aux deux portes par lesquelles on CRÉE des produits : le formulaire et
 * l'import. Les produits déjà sans prix se retrouvent par le filtre du
 * catalogue, et le formulaire exige le prix à leur prochaine modification.
 */
export const PRIX_ACHAT_OBLIGATOIRE = 'Le prix d’achat est obligatoire pour un produit';
export function prixAchatManquant(item) {
    return item.kind === 'product' && !(item.purchasePrice > 0);
}
export const itemQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    kind: itemKindSchema.optional(),
    categoryId: uuidSchema.optional(),
    supplierId: uuidSchema.optional(),
    status: itemStatusSchema.optional(),
    sort: z
        .enum(['name', '-name', 'salePrice', '-salePrice', 'sku', '-sku', 'createdAt', '-createdAt'])
        .default('name'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ------------------------------------------------------------- catégories
/**
 * Une catégorie sert des PRODUITS ou des SERVICES, jamais les deux.
 *
 * ⭐ C'est ce qui permet à la saisie d'un article de ne proposer que les
 * catégories qui ont un sens pour lui, et à l'agenda de savoir de quelles
 * colonnes il est fait : une colonne par catégorie de services.
 */
export const categoryKindSchema = z.enum(['product', 'service']);
export const CATEGORY_KIND_LABELS = {
    product: 'Produits',
    service: 'Services',
};
/**
 * Un POSTE : une place de travail dans une catégorie de services.
 *
 * ⭐ Trois fauteuils de coiffure, deux tables de soin. Le libellé est choisi
 * par la boutique — « Poste 2 » ne veut rien dire pour qui travaille dessus,
 * « Fauteuil vitrine » si.
 *
 * ⚠️ C'est le grain de la disponibilité : un rendez-vous par poste et par
 * créneau, pas un par boutique. Sans postes, une catégorie de services n'offre
 * aucune place — l'écran des catégories le dit plutôt que de laisser un agenda
 * vide inexpliqué.
 */
export const posteSchema = z.object({
    id: uuidSchema,
    label: z.string().trim().min(1, 'Libellé requis').max(60),
    position: z.coerce.number().int().min(0).default(0),
});
export const categoryInputSchema = z
    .object({
    id: uuidSchema.optional(),
    name: z.string().trim().min(2, 'Au moins 2 caractères').max(80),
    parentId: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
    color: optionalText(20),
    position: z.coerce.number().int().min(0).default(0),
    kind: categoryKindSchema.default('product'),
    postes: z.array(posteSchema).max(30, 'Trente postes au maximum').default([]),
})
    // ⚠️ Des postes sur une catégorie de PRODUITS n'auraient aucun sens et
    // apparaîtraient comme une colonne fantôme dans l'agenda. Le refus vaut mieux
    // qu'un silence : c'est le signe qu'on a changé le type sans vider la liste.
    .refine((v) => v.kind === 'service' || v.postes.length === 0, {
    message: 'Seule une catégorie de services porte des postes',
    path: ['postes'],
});
// ----------------------------------------------------------- fournisseurs
export const supplierInputSchema = z.object({
    id: uuidSchema.optional(),
    code: optionalText(40),
    companyName: z.string().trim().min(2, 'Au moins 2 caractères').max(160),
    contactName: optionalText(120),
    email: emailSchema.optional().or(z.literal('').transform(() => undefined)),
    phone: optionalText(30),
    website: optionalText(200),
    addressLine1: optionalText(160),
    postalCode: optionalText(20),
    city: optionalText(80),
    countryCode: z.string().length(2).toUpperCase().optional(),
    taxId: optionalText(60),
    paymentTermsDays: z.coerce.number().int().min(0).max(365).default(0),
    notes: optionalText(4000),
});
export const supplierQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    city: z.string().trim().max(80).optional(),
    sort: z.enum(['companyName', '-companyName', 'createdAt', '-createdAt']).default('companyName'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** Taux de TVA de la boutique. */
export const taxInputSchema = z.object({
    id: uuidSchema.optional(),
    name: z.string().trim().min(1, 'Libellé obligatoire').max(60),
    rate: z.coerce.number().min(0, 'Ne peut pas être négatif').max(100, 'Au plus 100 %'),
    isDefault: z.boolean().default(false),
    isActive: z.boolean().default(true),
});
/**
 * Prix toutes taxes comprises.
 *
 * ⭐ Le catalogue est saisi HORS TAXES — c'est la base de tous les calculs —
 * mais le prix affiché en rayon et annoncé au client est le TTC. Les deux
 * doivent se voir, sinon le commerçant fait la conversion de tête à chaque
 * fois et se trompe.
 */
export function prixTTC(prixHT, taux) {
    return Math.round(prixHT * (1 + taux / 100));
}
//# sourceMappingURL=catalog.js.map